import React, { useState } from "react";
import axios from "axios";
import { Search, Gauge, Tags, Star, Smartphone, AlertTriangle, ArrowRight, Loader2 } from "lucide-react";
import { useReveal } from "../hooks/useReveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const METRICS = [
  { key: "mobile", label: "Mobile readiness", icon: Smartphone },
  { key: "tags", label: "SEO tags", icon: Tags },
  { key: "reviews", label: "Review signals", icon: Star },
  { key: "performance", label: "Performance", icon: Gauge },
];

export default function FreeAudit() {
  const r = useReveal();
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const runAudit = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setLoading(true);
    setResult(null);
    setError("");
    try {
      const res = await axios.post(`${API}/audit`, { url: url.trim() });
      setResult(res.data);
    } catch (err) {
      setError("Couldn't run the audit right now. Try again, or call 832-280-5553.");
    } finally {
      setLoading(false);
    }
  };

  const barColor = (v) => (v >= 75 ? "#22c55e" : v >= 55 ? "#f26a21" : "#ef4444");
  const overallColor = result ? barColor(result.overall) : "#f26a21";

  return (
    <section id="audit" className="bg-ls py-20 md:py-28">
      <div ref={r.ref} className={`max-w-5xl mx-auto px-5 md:px-8 ${r.className}`}>
        <p className="ls-eyebrow mb-4">Not ready for a call?</p>
        <h2 className="font-display text-white text-[9vw] sm:text-5xl md:text-6xl mb-5">
          Audit your site yourself. <span className="text-orange">Free. Right now.</span>
        </h2>
        <p className="text-lg text-[#b7ab9b] max-w-3xl mb-10">
          Drop your website in. We fetch it live and check the stuff quietly costing you booked jobs — mobile
          readiness, SEO tags, review signals, and speed. No call, no commitment.
        </p>

        <div
          className="rounded-2xl p-6 md:p-8"
          style={{ background: "var(--ls-surface)", border: "1px solid var(--ls-border-strong)" }}
        >
          <form onSubmit={runAudit} className="flex flex-col sm:flex-row gap-3">
            <div
              className="flex items-center gap-3 flex-1 px-4 rounded-[6px]"
              style={{ background: "#05070b", border: "1px solid var(--ls-border)" }}
            >
              <Search className="w-5 h-5 text-[#9a8f80]" />
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="yourwebsite.com"
                className="bg-transparent py-3.5 w-full outline-none text-white placeholder:text-[#7d7264]"
              />
            </div>
            <button type="submit" className="btn-orange text-base justify-center min-w-[170px]" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Scanning…
                </>
              ) : (
                "Run Free Audit"
              )}
            </button>
          </form>
          <p className="text-xs text-[#9a8f80] mt-3 font-cond tracking-wide">
            Live scan of your homepage. No email required.
          </p>

          {error && <p className="text-red-400 text-sm mt-4">{error}</p>}

          {result && (
            <div className="mt-8">
              {/* overall */}
              <div className="flex items-center gap-4 mb-6">
                <div
                  className="flex flex-col items-center justify-center w-24 h-24 rounded-full shrink-0"
                  style={{ border: `4px solid ${overallColor}` }}
                >
                  <span className="font-display text-3xl text-white leading-none">{result.overall}</span>
                  <span className="text-[10px] text-[#b7ab9b] uppercase tracking-wide">/ 100</span>
                </div>
                <div>
                  <p className="font-cond font-bold text-xl text-white uppercase tracking-wide">
                    {result.reachable ? "Here's your read" : "Site unreachable"}
                  </p>
                  <p className="text-sm text-[#b7ab9b] break-all">{result.url}</p>
                  {result.reachable && result.response_time != null && (
                    <p className="text-sm text-[#b7ab9b]">Loaded in {result.response_time}s</p>
                  )}
                </div>
              </div>

              {/* metric bars */}
              <div className="grid sm:grid-cols-2 gap-5">
                {METRICS.map((m) => {
                  const v = result.scores?.[m.key] ?? 0;
                  const Icon = m.icon;
                  return (
                    <div key={m.key}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="flex items-center gap-2 text-[#ece5da] font-medium">
                          <Icon className="w-4 h-4 text-orange" /> {m.label}
                        </span>
                        <span className="font-cond font-bold text-white">{v}/100</span>
                      </div>
                      <div className="h-2.5 rounded-full overflow-hidden" style={{ background: "#05070b" }}>
                        <div
                          className="h-full rounded-full transition-all duration-700"
                          style={{ width: `${v}%`, backgroundColor: barColor(v) }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* findings */}
              {result.findings?.length > 0 && (
                <div className="mt-6 space-y-2.5">
                  {result.findings.map((f, i) => (
                    <div key={i} className="flex gap-3 items-start">
                      <AlertTriangle className="w-4 h-4 text-orange shrink-0 mt-1" />
                      <span className="text-[#d7cdbf]">{f}</span>
                    </div>
                  ))}
                </div>
              )}

              <a href="#cta" className="btn-orange text-base mt-7">
                Fix this for me <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
